/**
 * Leasing Hub - Browse Available Rental Units
 * For landlords, HOA/condo owners to list vacant units
 * Integrated with Estate Flow payment collection
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, MapPin, Bed, Bath, Square, DollarSign, 
  Home, Building2, Filter, Heart, Phone, Mail,
  Calendar, CheckCircle, Shield, Star, ChevronRight,
  X, SlidersHorizontal, Grid, List, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Demo rental listings with multiple photos
const DEMO_LISTINGS = [
  {
    id: 'demo-1',
    title: 'Modern 2BR Apartment in Cable Beach',
    type: 'apartment',
    owner_type: 'landlord',
    bedrooms: 2,
    bathrooms: 2,
    sqft: 1200,
    rent: 2800,
    deposit: 2800,
    location: 'Cable Beach, Nassau',
    address: '123 West Bay Street',
    description: 'Beautiful modern apartment with ocean views. Features include updated kitchen, in-unit laundry, and covered parking. Walking distance to beach and restaurants.',
    amenities: ['Air Conditioning', 'In-Unit Laundry', 'Parking', 'Pool Access', 'Gym', 'Pet Friendly'],
    images: [
      'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800',
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800',
      'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=800',
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800'
    ],
    available_date: '2026-02-15',
    lease_terms: ['12 months', '6 months'],
    utilities_included: ['Water', 'Trash'],
    contact_name: 'Robert Thompson',
    contact_phone: '+1-242-555-1234',
    featured: true,
    verified: true,
    created_at: '2026-02-01'
  },
  {
    id: 'demo-2',
    title: 'Luxury 3BR Condo at Lyford Cay',
    type: 'condo',
    owner_type: 'hoa_owner',
    bedrooms: 3,
    bathrooms: 2.5,
    sqft: 1800,
    rent: 4500,
    deposit: 4500,
    location: 'Lyford Cay, Nassau',
    address: '456 Lyford Cay Drive',
    description: 'Spacious luxury condo in gated community. High-end finishes, marble floors, and private balcony. HOA includes pool, tennis, and 24/7 security.',
    amenities: ['Air Conditioning', 'Balcony', 'Gated Community', 'Pool', 'Tennis Court', '24/7 Security', 'Parking'],
    images: [
      'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800',
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800',
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
      'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800'
    ],
    available_date: '2026-02-01',
    lease_terms: ['12 months'],
    utilities_included: ['Water', 'Trash', 'Cable'],
    contact_name: 'Patricia Williams',
    contact_phone: '+1-242-555-2345',
    featured: true,
    verified: true,
    created_at: '2026-01-28'
  },
  {
    id: 'demo-3',
    title: 'Cozy 1BR Studio Downtown',
    type: 'studio',
    owner_type: 'landlord',
    bedrooms: 1,
    bathrooms: 1,
    sqft: 650,
    rent: 1500,
    deposit: 1500,
    location: 'Downtown Nassau',
    address: '789 Bay Street',
    description: 'Perfect starter apartment in the heart of downtown. Recently renovated with modern appliances. Close to shops, restaurants, and public transport.',
    amenities: ['Air Conditioning', 'Updated Kitchen', 'Laundry On-Site'],
    images: [
      'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800',
      'https://images.unsplash.com/photo-1560185007-c5ca9d2c014d?w=800',
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800'
    ],
    available_date: '2026-02-10',
    lease_terms: ['12 months', '6 months', 'Month-to-Month'],
    utilities_included: ['Water'],
    contact_name: 'Michael Brown',
    contact_phone: '+1-242-555-3456',
    featured: false,
    verified: true,
    created_at: '2026-01-25'
  },
  {
    id: 'demo-4',
    title: 'Family 4BR House with Pool',
    type: 'house',
    owner_type: 'landlord',
    bedrooms: 4,
    bathrooms: 3,
    sqft: 2500,
    rent: 5500,
    deposit: 5500,
    location: 'Eastern Nassau',
    address: '321 Prince Charles Drive',
    description: 'Spacious family home with private pool and large backyard. Open floor plan, updated kitchen, and two-car garage. Great neighborhood with top-rated schools.',
    amenities: ['Air Conditioning', 'Private Pool', 'Backyard', 'Garage', 'Pet Friendly', 'Storage'],
    images: [
      'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800',
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800',
      'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=800',
      'https://images.unsplash.com/photo-1600573472550-8090b5e0745e?w=800',
      'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=800'
    ],
    available_date: '2026-03-01',
    lease_terms: ['12 months', '24 months'],
    utilities_included: [],
    contact_name: 'Sarah Wilson',
    contact_phone: '+1-242-555-4567',
    featured: true,
    verified: true,
    created_at: '2026-01-20'
  },
  {
    id: 'demo-5',
    title: '2BR Townhouse in Coral Harbour',
    type: 'townhouse',
    owner_type: 'hoa_owner',
    bedrooms: 2,
    bathrooms: 1.5,
    sqft: 1100,
    rent: 2200,
    deposit: 2200,
    location: 'Coral Harbour, Nassau',
    address: '555 Coral Way',
    description: 'Well-maintained townhouse in quiet community. Two stories with open living area downstairs and bedrooms upstairs. Community pool and playground.',
    amenities: ['Air Conditioning', 'Community Pool', 'Playground', 'Parking', 'Patio'],
    images: [
      'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?w=800',
      'https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?w=800',
      'https://images.unsplash.com/photo-1600210492493-0946911123ea?w=800'
    ],
    available_date: '2026-02-20',
    lease_terms: ['12 months'],
    utilities_included: ['Water', 'Trash'],
    contact_name: 'James Anderson',
    contact_phone: '+1-242-555-5678',
    featured: false,
    verified: true,
    created_at: '2026-01-15'
  },
  {
    id: 'demo-6',
    title: 'Beachfront 2BR Condo',
    type: 'condo',
    owner_type: 'hoa_owner',
    bedrooms: 2,
    bathrooms: 2,
    sqft: 1400,
    rent: 3800,
    deposit: 3800,
    location: 'Paradise Island',
    address: '100 Ocean Drive',
    description: 'Wake up to stunning ocean views! Direct beach access, resort-style amenities, and modern finishes throughout. Fully furnished option available.',
    amenities: ['Air Conditioning', 'Beach Access', 'Ocean View', 'Pool', 'Fitness Center', 'Concierge', 'Furnished Available'],
    images: [
      'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=800',
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800',
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
      'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800',
      'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800'
    ],
    available_date: '2026-02-01',
    lease_terms: ['12 months', '6 months'],
    utilities_included: ['Water', 'Cable', 'Internet'],
    contact_name: 'Elizabeth Moore',
    contact_phone: '+1-242-555-6789',
    featured: true,
    verified: true,
    created_at: '2026-01-10'
  }
];

const RentalMarketplace = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedListing, setSelectedListing] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [savedListings, setSavedListings] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    move_in_date: ''
  });
  
  // Filters
  const [filters, setFilters] = useState({
    minRent: '',
    maxRent: '',
    bedrooms: '',
    propertyType: '',
    location: ''
  });

  // Fetch listings from API on mount
  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/rental-marketplace/listings`);
      const data = await response.json();
      
      if (data.listings && data.listings.length > 0) {
        // Use real listings from database
        setListings(data.listings);
        setFilteredListings(data.listings);
      } else {
        // Fall back to demo listings if no real ones exist
        setListings(DEMO_LISTINGS);
        setFilteredListings(DEMO_LISTINGS);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
      // Fall back to demo listings on error
      setListings(DEMO_LISTINGS);
      setFilteredListings(DEMO_LISTINGS);
    }
    setLoading(false);
  };

  useEffect(() => {
    applyFilters();
  }, [searchQuery, filters, listings]);

  const applyFilters = () => {
    let result = [...listings];
    
    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(l => 
        l.title.toLowerCase().includes(query) ||
        l.location.toLowerCase().includes(query) ||
        l.description.toLowerCase().includes(query)
      );
    }
    
    // Rent range
    if (filters.minRent) {
      result = result.filter(l => l.rent >= parseInt(filters.minRent));
    }
    if (filters.maxRent) {
      result = result.filter(l => l.rent <= parseInt(filters.maxRent));
    }
    
    // Bedrooms
    if (filters.bedrooms) {
      result = result.filter(l => l.bedrooms >= parseInt(filters.bedrooms));
    }
    
    // Property type
    if (filters.propertyType) {
      result = result.filter(l => l.type === filters.propertyType);
    }
    
    // Location
    if (filters.location) {
      result = result.filter(l => l.location.toLowerCase().includes(filters.location.toLowerCase()));
    }
    
    setFilteredListings(result);
  };

  const toggleSaved = (listingId) => {
    if (savedListings.includes(listingId)) {
      setSavedListings(savedListings.filter(id => id !== listingId));
      toast.success('Removed from saved');
    } else {
      setSavedListings([...savedListings, listingId]);
      toast.success('Added to saved');
    }
  };

  const handleInquiry = async (listing) => {
    if (listing.id.startsWith('demo-')) {
      toast.info(
        <div>
          <p className="font-semibold">Demo Mode</p>
          <p className="text-sm">This is a demo listing. In production, this would send an inquiry to the property owner.</p>
        </div>
      );
    } else {
      setShowInquiryForm(true);
    }
  };

  const submitInquiry = async () => {
    if (!selectedListing) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/rental-marketplace/listings/${selectedListing.id}/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inquiryForm)
      });
      
      if (response.ok) {
        toast.success('Inquiry sent successfully! The property owner will contact you soon.');
        setShowInquiryForm(false);
        setInquiryForm({ name: '', email: '', phone: '', message: '', move_in_date: '' });
      } else {
        toast.error('Failed to send inquiry. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to send inquiry. Please try again.');
    }
  };

  const clearFilters = () => {
    setFilters({
      minRent: '',
      maxRent: '',
      bedrooms: '',
      propertyType: '',
      location: ''
    });
    setSearchQuery('');
  };

  const ListingCard = ({ listing }) => {
    const isDemo = listing.id?.startsWith('demo-');
    return (
      <Card 
        className={`overflow-hidden hover:shadow-lg transition-all cursor-pointer ${listing.featured ? 'ring-2 ring-emerald-500' : ''}`}
        onClick={() => { setCurrentImageIndex(0); setSelectedListing(listing); }}
        data-testid={`listing-card-${listing.id}`}
      >
        <div className="relative">
          <img 
            src={listing.images?.[0] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800'} 
            alt={listing.title}
            className="w-full h-48 object-cover"
          />
          {listing.featured && (
            <Badge className="absolute top-2 left-2 bg-emerald-500">Featured</Badge>
          )}
          {isDemo && (
            <Badge className="absolute top-2 right-2 bg-amber-500 text-amber-950">
              DEMO
            </Badge>
          )}
          {listing.verified && !isDemo && (
            <Badge className="absolute top-2 right-2 bg-emerald-500 text-white">
              Verified
            </Badge>
          )}
          {listing.images?.length > 1 && (
            <Badge className="absolute bottom-2 left-2 bg-black/70 text-white">
              {listing.images.length} Photos
            </Badge>
          )}
        <button 
          className={`absolute bottom-2 right-2 p-2 rounded-full ${savedListings.includes(listing.id) ? 'bg-red-500 text-white' : 'bg-white/90 text-gray-600'}`}
          onClick={(e) => { e.stopPropagation(); toggleSaved(listing.id); }}
        >
          <Heart className={`h-4 w-4 ${savedListings.includes(listing.id) ? 'fill-white' : ''}`} />
        </button>
      </div>
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="font-semibold text-lg line-clamp-1">{listing.title}</h3>
        </div>
        <div className="flex items-center text-gray-500 text-sm mb-3">
          <MapPin className="h-4 w-4 mr-1" />
          {listing.location}
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
          <span className="flex items-center gap-1">
            <Bed className="h-4 w-4" /> {listing.bedrooms} bed
          </span>
          <span className="flex items-center gap-1">
            <Bath className="h-4 w-4" /> {listing.bathrooms} bath
          </span>
          <span className="flex items-center gap-1">
            <Square className="h-4 w-4" /> {listing.sqft} sqft
          </span>
        </div>
        <div className="flex justify-between items-center">
          <div>
            <span className="text-2xl font-bold text-emerald-600">${listing.rent?.toLocaleString()}</span>
            <span className="text-gray-500">/month</span>
          </div>
          {listing.verified && !isDemo && (
            <Badge variant="outline" className="text-emerald-600 border-emerald-600">
              <CheckCircle className="h-3 w-3 mr-1" /> Verified
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
    );
  };

  const ListingDetail = ({ listing, onClose }) => {
    const isDemo = listing.id?.startsWith('demo-');
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          {/* Demo Banner - only show for demo listings */}
          {isDemo && (
            <div className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-semibold">DEMO LISTING</span>
              <span className="text-sm">- This is a sample listing for demonstration purposes</span>
            </div>
          )}
          
          {/* Photo Gallery */}
          <div className="relative">
            <img 
              src={listing.images?.[currentImageIndex || 0] || 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800'} 
              alt={listing.title}
              className="w-full h-64 md:h-80 object-cover"
            />
            <button 
              className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg z-10"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </button>
            {listing.featured && (
              <Badge className="absolute top-4 left-4 bg-emerald-500">Featured Listing</Badge>
            )}
            
            {/* Gallery Navigation */}
            {listing.images?.length > 1 && (
              <>
                <button 
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg"
                  onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => prev === 0 ? listing.images.length - 1 : prev - 1); }}
                >
                  <ChevronRight className="h-5 w-5 rotate-180" />
                </button>
                <button 
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg"
                  onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => prev === listing.images.length - 1 ? 0 : prev + 1); }}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                  {(currentImageIndex || 0) + 1} / {listing.images.length}
                </div>
              </>
            )}
          </div>
        
        {/* Thumbnail Strip */}
        {listing.images?.length > 1 && (
          <div className="flex gap-2 p-2 bg-gray-100 overflow-x-auto">
            {listing.images.map((img, idx) => (
              <img 
                key={idx}
                src={img}
                alt={`${listing.title} ${idx + 1}`}
                className={`w-20 h-14 object-cover rounded cursor-pointer flex-shrink-0 ${idx === (currentImageIndex || 0) ? 'ring-2 ring-emerald-500' : 'opacity-70 hover:opacity-100'}`}
                onClick={() => setCurrentImageIndex(idx)}
              />
            ))}
          </div>
        )}
        
        <div className="p-6">
          <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
            <div>
              <h2 className="text-2xl font-bold">{listing.title}</h2>
              <div className="flex items-center text-gray-500 mt-1">
                <MapPin className="h-4 w-4 mr-1" />
                {listing.address}, {listing.location}
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-emerald-600">${listing.rent?.toLocaleString()}</div>
              <div className="text-gray-500">per month</div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-4 py-4 border-y">
            <div className="flex items-center gap-2">
              <Bed className="h-5 w-5 text-gray-400" />
              <span><strong>{listing.bedrooms}</strong> Bedrooms</span>
            </div>
            <div className="flex items-center gap-2">
              <Bath className="h-5 w-5 text-gray-400" />
              <span><strong>{listing.bathrooms}</strong> Bathrooms</span>
            </div>
            <div className="flex items-center gap-2">
              <Square className="h-5 w-5 text-gray-400" />
              <span><strong>{listing.sqft?.toLocaleString()}</strong> sqft</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-400" />
              <span>Available <strong>{new Date(listing.available_date).toLocaleDateString()}</strong></span>
            </div>
          </div>
          
          <div className="py-4">
            <h3 className="font-semibold text-lg mb-2">Description</h3>
            <p className="text-gray-600">{listing.description}</p>
          </div>
          
          <div className="py-4 border-t">
            <h3 className="font-semibold text-lg mb-3">Amenities</h3>
            <div className="flex flex-wrap gap-2">
              {listing.amenities?.map((amenity, idx) => (
                <Badge key={idx} variant="secondary">{amenity}</Badge>
              ))}
            </div>
          </div>
          
          <div className="py-4 border-t">
            <h3 className="font-semibold text-lg mb-3">Lease Details</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <span className="text-gray-500">Security Deposit:</span>
                <span className="ml-2 font-semibold">${listing.deposit?.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-gray-500">Lease Terms:</span>
                <span className="ml-2 font-semibold">{listing.lease_terms?.join(', ')}</span>
              </div>
              {listing.utilities_included?.length > 0 && (
                <div className="md:col-span-2">
                  <span className="text-gray-500">Utilities Included:</span>
                  <span className="ml-2 font-semibold">{listing.utilities_included.join(', ')}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="py-4 border-t">
            <h3 className="font-semibold text-lg mb-3">Contact Information</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Home className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <div className="font-semibold">{listing.contact_name}</div>
                  <div className="text-sm text-gray-500">
                    {listing.owner_type === 'landlord' ? 'Property Owner' : 'Unit Owner'}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4 mt-3">
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="h-4 w-4" />
                  {listing.contact_phone}
                </div>
              </div>
            </div>
          </div>
          
          {/* Inquiry Form */}
          {showInquiryForm && !isDemo && (
            <div className="py-4 border-t">
              <h3 className="font-semibold text-lg mb-3">Send an Inquiry</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Your Name"
                  value={inquiryForm.name}
                  onChange={(e) => setInquiryForm({...inquiryForm, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
                <input
                  type="email"
                  placeholder="Your Email"
                  value={inquiryForm.email}
                  onChange={(e) => setInquiryForm({...inquiryForm, email: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
                <input
                  type="tel"
                  placeholder="Your Phone"
                  value={inquiryForm.phone}
                  onChange={(e) => setInquiryForm({...inquiryForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
                <input
                  type="date"
                  placeholder="Preferred Move-in Date"
                  value={inquiryForm.move_in_date}
                  onChange={(e) => setInquiryForm({...inquiryForm, move_in_date: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
                <textarea
                  placeholder="Your Message"
                  value={inquiryForm.message}
                  onChange={(e) => setInquiryForm({...inquiryForm, message: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button onClick={submitInquiry} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    Send Inquiry
                  </Button>
                  <Button variant="outline" onClick={() => setShowInquiryForm(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap gap-3 pt-4">
            <Button 
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              onClick={() => handleInquiry(listing)}
            >
              <Mail className="h-4 w-4 mr-2" />
              Send Inquiry
            </Button>
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => handleInquiry(listing)}
            >
              <Phone className="h-4 w-4 mr-2" />
              Schedule Viewing
            </Button>
            <Button
              variant="outline"
              onClick={() => toggleSaved(listing.id)}
            >
              <Heart className={`h-4 w-4 ${savedListings.includes(listing.id) ? 'fill-red-500 text-red-500' : ''}`} />
            </Button>
          </div>
          
          <div className="mt-6 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-emerald-800">Secure Payments via Estate Flow</h4>
                <p className="text-sm text-emerald-700 mt-1">
                  Rent payments are processed securely through our platform. Both landlords and tenants are protected with our payment guarantee.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Leasing Hub</h1>
              <p className="text-emerald-100 mt-2">Find your perfect rental home</p>
            </div>
            <Button 
              variant="outline" 
              className="text-white border-white hover:bg-white/10"
              onClick={() => navigate('/')}
            >
              Back to Home
            </Button>
          </div>
          
          {/* Demo Notice */}
          <div className="bg-amber-500/20 border border-amber-400/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-300" />
              <span className="font-semibold">Demo Marketplace</span>
            </div>
            <p className="text-sm text-emerald-100 mt-1">
              These are sample listings for demonstration purposes. Landlords and HOA owners can list their vacant units here to find qualified tenants.
            </p>
          </div>
          
          {/* Search Bar */}
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input 
                placeholder="Search by location, property type, or keywords..."
                className="pl-10 h-12 bg-white text-gray-900"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Button 
              variant="secondary" 
              className="h-12 px-6"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Filters
            </Button>
          </div>
          
          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-white rounded-lg p-4 mt-4 text-gray-900">
              <div className="grid md:grid-cols-5 gap-4">
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Min Rent</label>
                  <Input 
                    type="number"
                    placeholder="$0"
                    value={filters.minRent}
                    onChange={(e) => setFilters({...filters, minRent: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Max Rent</label>
                  <Input 
                    type="number"
                    placeholder="$10,000+"
                    value={filters.maxRent}
                    onChange={(e) => setFilters({...filters, maxRent: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Bedrooms</label>
                  <select 
                    className="w-full h-10 px-3 rounded-md border"
                    value={filters.bedrooms}
                    onChange={(e) => setFilters({...filters, bedrooms: e.target.value})}
                  >
                    <option value="">Any</option>
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Property Type</label>
                  <select 
                    className="w-full h-10 px-3 rounded-md border"
                    value={filters.propertyType}
                    onChange={(e) => setFilters({...filters, propertyType: e.target.value})}
                  >
                    <option value="">All Types</option>
                    <option value="apartment">Apartment</option>
                    <option value="condo">Condo</option>
                    <option value="house">House</option>
                    <option value="townhouse">Townhouse</option>
                    <option value="studio">Studio</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Location</label>
                  <Input 
                    placeholder="e.g. Nassau"
                    value={filters.location}
                    onChange={(e) => setFilters({...filters, location: e.target.value})}
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4">
                <Button variant="ghost" onClick={clearFilters}>
                  Clear All Filters
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Results Header */}
        <div className="flex flex-wrap justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold">{filteredListings.length} Rentals Available</h2>
            <p className="text-gray-500 text-sm">Updated just now</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant={viewMode === 'grid' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid className="h-4 w-4" />
            </Button>
            <Button 
              variant={viewMode === 'list' ? 'default' : 'outline'} 
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        {/* Featured Listings */}
        {filteredListings.filter(l => l.featured).length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" />
              Featured Listings
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredListings.filter(l => l.featured).map(listing => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          </div>
        )}
        
        {/* All Listings */}
        <div>
          <h3 className="text-lg font-semibold mb-4">All Listings</h3>
          {filteredListings.length > 0 ? (
            <div className={viewMode === 'grid' ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
              {filteredListings.map(listing => (
                <ListingCard key={listing.id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg">
              <Home className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600">No listings found</h3>
              <p className="text-gray-500 mt-1">Try adjusting your filters or search query</p>
              <Button variant="outline" className="mt-4" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          )}
        </div>
        
        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-8 text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Have a Vacant Unit?</h2>
            <p className="text-emerald-100 mb-6">
              List your property on our marketplace and find qualified tenants. 
              Rent payments are collected securely through Estate Flow.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg" 
                className="bg-white text-emerald-600 hover:bg-emerald-50"
                onClick={() => navigate('/landlord/login')}
              >
                <Building2 className="h-5 w-5 mr-2" />
                I'm a Landlord
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-white text-white hover:bg-white/10"
                onClick={() => navigate('/hoa-owner/login')}
              >
                <Home className="h-5 w-5 mr-2" />
                I'm a Condo/HOA Owner
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Listing Detail Modal */}
      {selectedListing && (
        <ListingDetail listing={selectedListing} onClose={() => setSelectedListing(null)} />
      )}
    </div>
  );
};

export default RentalMarketplace;
